import { useCallback, useEffect, useRef, useState } from 'react';
import { checkPhoneDuplicateApi } from '../utils/api';

const DEBOUNCE_MS = 400;
const MIN_DIGITS = 8;

export const digitsOnlyPhone = (value) => {
  if (value == null) return '';
  return String(value)
    .replace(/[۰-۹]/g, (d) => String('۰۱۲۳۴۵۶۷۸۹'.indexOf(d)))
    .replace(/[٠-٩]/g, (d) => String('٠١٢٣٤٥٦٧٨٩'.indexOf(d)))
    .replace(/\D/g, '');
};

/**
 * استعلام بلادرنگ تکراری بودن شماره تماس
 * @param {{ excludeLeadId?: number|string, excludeClientId?: number|string }} options
 */
export function usePhoneDuplicateCheck({ excludeLeadId, excludeClientId } = {}) {
  const [duplicateInfo, setDuplicateInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const timerRef = useRef(null);
  const requestIdRef = useRef(0);

  const clearDuplicate = useCallback(() => {
    setDuplicateInfo(null);
  }, []);

  const checkPhone = useCallback(
    (phoneNumber) => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }

      const cleanNumber = digitsOnlyPhone(phoneNumber);
      if (cleanNumber.length < MIN_DIGITS) {
        setDuplicateInfo(null);
        setLoading(false);
        return;
      }

      setLoading(true);
      const requestId = ++requestIdRef.current;

      timerRef.current = setTimeout(async () => {
        try {
          const params = { phone: cleanNumber };
          if (excludeLeadId != null && excludeLeadId !== '') {
            params.exclude_lead_id = excludeLeadId;
          }
          if (excludeClientId != null && excludeClientId !== '') {
            params.exclude_client_id = excludeClientId;
          }
          const res = await checkPhoneDuplicateApi(params);
          if (requestId !== requestIdRef.current) return;
          const data = res?.data ?? res ?? {};
          if (data.exists) {
            setDuplicateInfo(data);
          } else {
            setDuplicateInfo(null);
          }
        } catch (error) {
          if (requestId !== requestIdRef.current) return;
          // خطای شبکه نباید کاربر را بلاک کند
          console.error('Phone duplicate check failed:', error);
          setDuplicateInfo(null);
        } finally {
          if (requestId === requestIdRef.current) {
            setLoading(false);
          }
        }
      }, DEBOUNCE_MS);
    },
    [excludeLeadId, excludeClientId],
  );

  useEffect(
    () => () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      requestIdRef.current += 1;
    },
    [],
  );

  return {
    checkPhone,
    clearDuplicate,
    duplicateInfo,
    loading,
    hasDuplicate: Boolean(duplicateInfo?.exists),
  };
}

export default usePhoneDuplicateCheck;
